/**
 * Client-side public join storage (demo + local submissions).
 * Public survey UI lives in bms-client; admin only builds/publishes trackers.
 */

const TRACKERS_KEY = "gymrite_public_join_trackers";
const SUBMISSIONS_KEY = "gymrite_public_join_submissions";
const DEMO_FLAG_KEY = "gymrite_public_join_demo_v2";

export const TRACKER_STATUS = {
  DRAFT: "draft",
  LIVE: "live",
  ARCHIVED: "archived",
};

export const FORM_QUESTION_SOURCES = {
  LIBRARY: "library",
  TRACKER: "tracker",
  SHARED: "shared",
  PERSONAL_INFO: "personal_info",
};

export const PERSONAL_INFO_LIBRARY_IDS = [
  "q_first_name",
  "q_last_name",
  "q_email",
  "q_phone",
];

export const isPersonalInfoLibraryId = (questionId) =>
  PERSONAL_INFO_LIBRARY_IDS.includes(questionId);

export const isLeadCaptureTracker = (tracker) =>
  tracker?.category === "lead_capture";

export const isTrackerPublicLinkAvailable = (tracker) =>
  String(tracker?.trackerAccess || "public").toLowerCase() === "public";

const q = (id, data = {}, order = 0) => ({
  id,
  source: data.source || FORM_QUESTION_SOURCES.TRACKER,
  questionId: data.questionId || null,
  label: data.label || "",
  placeholder: data.placeholder || "",
  helpText: data.helpText || "",
  helperText: data.helperText || data.helpText || "",
  description: data.description || "",
  required: Boolean(data.required),
  searchable: data.searchable !== false,
  filterable: data.filterable !== false,
  measurable: Boolean(data.measurable),
  order,
  mode: data.measurable ? "measurable" : data.mode || "general",
  fieldType: data.fieldType || "short_text",
  measureType: data.measureType || "",
  units: data.units || [],
  defaultUnit: data.defaultUnit || "",
  measureDirection: data.measureDirection || "descending",
  scaleMin: data.scaleMin ?? 1,
  scaleMax: data.scaleMax ?? 10,
  options: data.options || [],
});

const personalDefaults = () => [
  q(
    "personal_q_first_name",
    {
      source: FORM_QUESTION_SOURCES.PERSONAL_INFO,
      questionId: "q_first_name",
      label: "First Name",
      placeholder: "Enter first name",
      fieldType: "short_text",
      required: true,
    },
    0
  ),
  q(
    "personal_q_last_name",
    {
      source: FORM_QUESTION_SOURCES.PERSONAL_INFO,
      questionId: "q_last_name",
      label: "Last Name",
      placeholder: "Enter last name",
      fieldType: "short_text",
      required: true,
    },
    1
  ),
  q(
    "personal_q_email",
    {
      source: FORM_QUESTION_SOURCES.PERSONAL_INFO,
      questionId: "q_email",
      label: "Email Address",
      placeholder: "Enter email address",
      fieldType: "email",
      required: true,
    },
    2
  ),
  q(
    "personal_q_phone",
    {
      source: FORM_QUESTION_SOURCES.PERSONAL_INFO,
      questionId: "q_phone",
      label: "Phone Number",
      placeholder: "Enter phone number",
      fieldType: "phone",
      required: true,
    },
    3
  ),
];

const buildDemoTrackers = () => {
  const personal = personalDefaults();

  const leadCapture = {
    id: "tracker_lead_capture_demo",
    name: "Join a Programme — Lead Capture",
    description: "Public lead form for ads, QR and website campaigns.",
    visitorMessage:
      "Interested in training with us? Share your details, then answer a few questions.",
    kind: "standard",
    category: "lead_capture",
    publicSlug: "join-programme-lead",
    status: TRACKER_STATUS.LIVE,
    trackerAccess: "public",
    personalInfoQuestions: [
      ...personal,
      q(
        "personal_city",
        {
          source: FORM_QUESTION_SOURCES.PERSONAL_INFO,
          label: "City / Area",
          placeholder: "Where are you based?",
          fieldType: "short_text",
          required: true,
        },
        4
      ),
    ],
    sharedQuestions: [
      q(
        "lead_extra_rate",
        {
          source: FORM_QUESTION_SOURCES.SHARED,
          label: "How would you rate your overall experience with fitness so far?",
          fieldType: "multiple_choice",
          options: ["Amazing", "Good", "Average", "Poor", "Extremely Poor"],
          required: true,
        },
        0
      ),
      q(
        "lead_extra_times",
        {
          source: FORM_QUESTION_SOURCES.SHARED,
          label: "How often do you want to train?",
          fieldType: "checkbox",
          options: [
            "Everyday",
            "Fairly often",
            "Sometimes",
            "Not that often",
            "Rarely",
          ],
          required: true,
        },
        1
      ),
      q(
        "lead_extra_story",
        {
          source: FORM_QUESTION_SOURCES.SHARED,
          label: "Tell us about your experience and what you're looking for",
          fieldType: "long_text",
          placeholder: "Goals, history, anything that helps us help you…",
          required: true,
        },
        2
      ),
      q(
        "lead_extra_feel",
        {
          source: FORM_QUESTION_SOURCES.SHARED,
          label: "How do you feel about starting a new programme?",
          fieldType: "scale",
          required: true,
        },
        3
      ),
      q(
        "lead_extra_goal",
        {
          source: FORM_QUESTION_SOURCES.SHARED,
          label: "What is your main goal right now?",
          fieldType: "dropdown",
          options: [
            "Lose fat",
            "Build muscle",
            "Get stronger",
            "Improve fitness",
            "Feel healthier",
          ],
          required: true,
        },
        4
      ),
    ],
    forms: [
      {
        id: "form_lead_main",
        name: "Lead form",
        role: "first_submission",
        questions: [],
      },
    ],
    statuses: [
      { id: "st_lead_new", name: "New Lead", color: "#0369a1", assignedOnSubmit: true },
    ],
    coaches: [
      {
        id: "coach_demo_1",
        first_name: "Sarah",
        last_name: "Murphy",
        email: "sarah.murphy@gymrite.demo",
      },
    ],
    settings: {
      requirePassword: false,
      password: "",
      acceptingSubmissions: true,
      confirmationMessage:
        "Thanks — we've got your details. A coach will call you back soon.",
    },
  };

  const chasingBetter = {
    id: "tracker_chasing_better",
    name: "Chasing Better Journey",
    description: "Ongoing transformation journey intake.",
    visitorMessage:
      "Welcome to Chasing Better. Complete your baseline to get started.",
    kind: "journey",
    category: "journey",
    publicSlug: "chasing-better-journey",
    status: TRACKER_STATUS.LIVE,
    trackerAccess: "public",
    personalInfoQuestions: personal,
    sharedQuestions: [
      q(
        "shared_cb_gym",
        {
          source: FORM_QUESTION_SOURCES.SHARED,
          label: "Preferred gym location",
          fieldType: "dropdown",
          options: ["City Centre", "North", "South", "Online only"],
          required: true,
        },
        0
      ),
      q(
        "shared_cb_focus",
        {
          source: FORM_QUESTION_SOURCES.SHARED,
          label: "Primary training focus for the next 30 days",
          fieldType: "short_text",
          placeholder: "e.g. strength, fat loss, mobility",
          required: true,
        },
        1
      ),
    ],
    forms: [
      {
        id: "form_cb_signup",
        name: "Sign-up & Baseline",
        role: "first_submission",
        questions: [
          q(
            "tq_cb_story",
            {
              label: "Tell us about your fitness background",
              fieldType: "long_text",
              placeholder: "Gym history, sports, injuries…",
              required: true,
            },
            0
          ),
          q(
            "tq_cb_goal",
            {
              label: "What is your main goal right now?",
              fieldType: "multiple_choice",
              options: [
                "Lose fat",
                "Build muscle",
                "Get stronger",
                "Improve fitness",
                "Feel healthier",
              ],
              required: true,
            },
            1
          ),
          q(
            "tq_cb_days",
            {
              label: "Which days can you usually train?",
              fieldType: "checkbox",
              options: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
              required: true,
            },
            2
          ),
          q(
            "tq_cb_weight",
            {
              label: "Starting weight (kg)",
              fieldType: "number",
              placeholder: "e.g. 78",
              required: true,
              measurable: true,
              measureType: "weight",
              units: ["kg"],
              defaultUnit: "kg",
            },
            3
          ),
          q(
            "tq_cb_target",
            {
              label: "Target weight (kg)",
              fieldType: "number",
              placeholder: "e.g. 72",
              required: false,
              measurable: true,
              measureType: "weight",
              units: ["kg"],
              defaultUnit: "kg",
            },
            4
          ),
          q(
            "tq_cb_energy",
            {
              label: "Current energy level (1–10)",
              fieldType: "scale",
              options: ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"],
              required: true,
              measurable: true,
              measureType: "scale",
            },
            5
          ),
        ],
      },
    ],
    statuses: [
      {
        id: "st_cb_enrolled",
        name: "Enrolled",
        color: "#0369a1",
        assignedOnSubmit: true,
      },
    ],
    coaches: [
      {
        id: "coach_demo_1",
        first_name: "Sarah",
        last_name: "Murphy",
        email: "sarah.murphy@gymrite.demo",
      },
    ],
    settings: {
      requirePassword: false,
      password: "",
      acceptingSubmissions: true,
      confirmationMessage: "Thanks — you're enrolled. A coach will be in touch.",
    },
  };

  const summerChallenge = {
    id: "tracker_summer_challenge_public",
    name: "Summer Challenge 2026",
    description: "Gym-wide challenge with weekly targets.",
    visitorMessage:
      "Join the pack. Hit weekly targets, climb the board, finish strong.",
    kind: "journey",
    category: "challenge",
    publicSlug: "summer-challenge-2026",
    status: TRACKER_STATUS.LIVE,
    trackerAccess: "public",
    personalInfoQuestions: personal,
    sharedQuestions: [
      q(
        "ch_goal",
        {
          source: FORM_QUESTION_SOURCES.SHARED,
          label: "What are you chasing this summer?",
          fieldType: "multiple_choice",
          options: [
            "Fat loss",
            "Strength",
            "Consistency",
            "Community",
            "All of it",
          ],
          required: true,
        },
        0
      ),
      q(
        "ch_days",
        {
          source: FORM_QUESTION_SOURCES.SHARED,
          label: "How many sessions can you commit to per week?",
          fieldType: "dropdown",
          options: ["2", "3", "4", "5+"],
          required: true,
        },
        1
      ),
    ],
    forms: [
      {
        id: "form_ch_join",
        name: "Challenge join",
        role: "first_submission",
        questions: [],
      },
    ],
    statuses: [
      { id: "st_ch_in", name: "Joined", color: "#047857", assignedOnSubmit: true },
    ],
    coaches: [],
    settings: {
      requirePassword: false,
      password: "",
      acceptingSubmissions: true,
      confirmationMessage: "You're in the challenge — check the board this week.",
    },
  };

  const freeConsult = {
    id: "tracker_free_consult_public",
    name: "Free Consultation",
    description: "Warm lead consult booking.",
    visitorMessage:
      "Tell us a bit about you — we’ll match a coach and confirm a time.",
    kind: "standard",
    category: "lead_capture",
    publicSlug: "free-consultation",
    status: TRACKER_STATUS.LIVE,
    trackerAccess: "public",
    personalInfoQuestions: personal,
    sharedQuestions: [
      q(
        "fc_goal",
        {
          source: FORM_QUESTION_SOURCES.SHARED,
          label: "What’s your main goal right now?",
          fieldType: "short_text",
          required: true,
        },
        0
      ),
      q(
        "fc_when",
        {
          source: FORM_QUESTION_SOURCES.SHARED,
          label: "When works best for a chat?",
          fieldType: "dropdown",
          options: ["Morning", "Lunch", "Evening", "Weekend"],
          required: true,
        },
        1
      ),
    ],
    forms: [
      {
        id: "form_fc_main",
        name: "Consult form",
        role: "first_submission",
        questions: [],
      },
    ],
    statuses: [
      { id: "st_fc_new", name: "New Lead", color: "#0369a1", assignedOnSubmit: true },
    ],
    coaches: [],
    settings: {
      requirePassword: false,
      password: "",
      acceptingSubmissions: true,
      confirmationMessage: "Thanks — a coach will confirm your consult shortly.",
    },
  };

  return [leadCapture, chasingBetter, summerChallenge, freeConsult];
};

const readJson = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
};

const ensureDemoSeeded = () => {
  // Public join is API-backed — do not inject local dummy trackers.
  try {
    localStorage.removeItem(TRACKERS_KEY);
    localStorage.removeItem(DEMO_FLAG_KEY);
  } catch {
    /* ignore */
  }
  return [];
};

export const getPublicTrackers = () => ensureDemoSeeded();

const slugifyQuestionKey = (value = "") =>
  String(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "") || "q";

/** Ensure every question has a stable id (and personal library ids when obvious). */
const normalizeQuestionList = (list = [], prefix = "q") =>
  (Array.isArray(list) ? list : []).map((item, index) => {
    if (!item || typeof item !== "object") return item;
    const label = String(item.label || "").toLowerCase();
    let questionId = item.questionId || null;
    if (!questionId) {
      if (label.includes("first name")) questionId = "q_first_name";
      else if (label.includes("last name")) questionId = "q_last_name";
      else if (label.includes("email")) questionId = "q_email";
      else if (label.includes("phone")) questionId = "q_phone";
    }
    const id =
      item.id ||
      (questionId ? `field_${questionId}` : `${prefix}_${index}_${slugifyQuestionKey(item.label)}`);
    return {
      ...item,
      id,
      questionId,
      fieldType: item.fieldType || "short_text",
      required: Boolean(item.required),
    };
  });

/** Soft normalize — client demos are already shaped. */
export const ensureTrackerShape = (tracker) => {
  if (!tracker) return null;
  const forms = (Array.isArray(tracker.forms) ? tracker.forms : []).map(
    (form, formIndex) => ({
      ...form,
      id: form?.id || `form_${formIndex}`,
      questions: normalizeQuestionList(form?.questions || [], `form${formIndex}_q`),
    })
  );
  return {
    ...tracker,
    personalInfoQuestions: normalizeQuestionList(
      tracker.personalInfoQuestions || [],
      "personal"
    ),
    sharedQuestions: normalizeQuestionList(tracker.sharedQuestions || [], "shared"),
    forms,
    settings: tracker.settings || { acceptingSubmissions: true },
    statuses: Array.isArray(tracker.statuses) ? tracker.statuses : [],
    coaches: Array.isArray(tracker.coaches) ? tracker.coaches : [],
  };
};

export const getTrackerByPublicSlug = (slug) => {
  const needle = String(slug || "")
    .trim()
    .toLowerCase();
  if (!needle) return null;
  return (
    getPublicTrackers().find((tracker) => {
      const publicSlug = String(tracker.publicSlug || "")
        .trim()
        .toLowerCase();
      const id = String(tracker.id || "")
        .trim()
        .toLowerCase();
      return publicSlug === needle || id === needle;
    }) || null
  );
};

const isFirstSubmissionForm = (form, allForms = []) => {
  if (!form) return false;
  if (form.role === "first_submission") return true;
  if (!allForms.length) return false;
  const designated = allForms.find((f) => f.role === "first_submission");
  if (designated) return designated.id === form.id;
  return allForms[0]?.id === form.id;
};

/** Personal (first submit) + shared + form-specific. */
export const getEffectiveFormQuestions = (
  form,
  sharedQuestions = [],
  personalInfoQuestions = [],
  allForms = []
) => {
  const excluded = new Set(form?.excludedSharedQuestionIds || []);
  const overrides = form?.sharedOverrides || {};
  let order = 0;

  const personal = isFirstSubmissionForm(form, allForms)
    ? (personalInfoQuestions || []).map((item) => {
        const override = overrides[item.id] || {};
        return {
          ...item,
          ...override,
          required: true,
          source: FORM_QUESTION_SOURCES.PERSONAL_INFO,
          order: order++,
        };
      })
    : [];

  const shared = (sharedQuestions || [])
    .filter(
      (item) =>
        item &&
        !excluded.has(item.id) &&
        !isPersonalInfoLibraryId(item.questionId)
    )
    .map((item) => {
      const override = overrides[item.id] || {};
      return {
        ...item,
        ...override,
        source: FORM_QUESTION_SOURCES.SHARED,
        order: order++,
      };
    });

  const specific = (form?.questions || [])
    .filter((item) => !isPersonalInfoLibraryId(item?.questionId))
    .map((item) => ({ ...item, order: order++ }));

  return [...personal, ...shared, ...specific];
};

export const createPublicSubmission = ({
  tracker,
  form,
  answers = {},
  questions = [],
} = {}) => {
  if (!tracker?.id) return null;

  const byLibraryId = {};
  (questions || []).forEach((item) => {
    if (item?.questionId) byLibraryId[item.questionId] = answers[item.id];
  });

  const firstName = String(
    byLibraryId.q_first_name || answers.firstName || ""
  ).trim();
  const lastName = String(
    byLibraryId.q_last_name || answers.lastName || ""
  ).trim();
  const email = String(byLibraryId.q_email || answers.email || "").trim();
  const phone = String(byLibraryId.q_phone || answers.phone || "").trim();

  const defaultStatus =
    (tracker.statuses || []).find((s) => s.assignedOnSubmit) ||
    (tracker.statuses || [])[0] ||
    null;

  const row = {
    id: `sub_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    trackerId: tracker.id,
    trackerName: tracker.name || "",
    formId: form?.id || "",
    formName: form?.name || "Lead form",
    source: "public",
    firstName,
    lastName,
    email,
    phone,
    memberName: `${firstName} ${lastName}`.trim() || email || "Public lead",
    statusName: defaultStatus?.name || "New Lead",
    statusColor: defaultStatus?.color || "#0369a1",
    answers,
    submittedAt: new Date().toISOString(),
    submittedBy: "public",
  };

  const list = readJson(SUBMISSIONS_KEY, []);
  list.unshift(row);
  localStorage.setItem(SUBMISSIONS_KEY, JSON.stringify(list));
  return row;
};

/** Dev helper — force reseed demo trackers. */
export const reloadPublicJoinDemos = () => {
  localStorage.removeItem(DEMO_FLAG_KEY);
  return ensureDemoSeeded();
};
